import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from '@/styles';
import { axiosRubyClient } from '@/util/axios/axiosClient';

const Container = styled('div', {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'white',
  padding: '20px',
});

const Content = styled('div', {
  maxWidth: '480px',
  width: '100%',
  textAlign: 'center',
});

const Title = styled('h1', {
  fontSize: '28px',
  color: '#0E1E40',
  marginBottom: '24px',
  fontWeight: '600',
});

const StatusContainer = styled('div', {
  padding: '40px 32px',
  borderRadius: '12px',
  marginTop: '24px',
});

const LoadingContainer = styled(StatusContainer, {
  background: '#F7F9FC',
});

const SuccessContainer = styled(StatusContainer, {
  background: '#E6F7ED',
});

const ErrorContainer = styled(StatusContainer, {
  background: '#FEF2F2',
});

const Spinner = styled('div', {
  border: '3px solid #E7E8EA',
  borderTop: '3px solid #52C241',
  borderRadius: '50%',
  width: '48px',
  height: '48px',
  animation: 'spin 1s linear infinite',
  margin: '0 auto 16px',

  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
});

const CheckIcon = styled('svg', {
  width: '64px',
  height: '64px',
  fill: '#52C241',
  margin: '0 auto 16px',
  display: 'block',
});

const ErrorIcon = styled('svg', {
  width: '64px',
  height: '64px',
  fill: '#E53E3E',
  margin: '0 auto 16px',
  display: 'block',
});

const Message = styled('p', {
  fontSize: '16px',
  lineHeight: '1.6',
  color: '#4A5568',
  marginBottom: '24px',
});

const RetryButton = styled('button', {
  padding: '12px 32px',
  borderRadius: '8px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  border: 'none',
  background: '#52C241',
  color: 'white',
  transition: 'all 0.2s ease',

  '&:hover': {
    background: '#45A735',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(82, 194, 65, 0.4)',
  },

  '&:active': {
    transform: 'translateY(0)',
  },
});

const SecondaryButton = styled(RetryButton, {
  background: '#718096',

  '&:hover': {
    background: '#4A5568',
    boxShadow: '0 4px 12px rgba(113, 128, 150, 0.4)',
  },
});

type Status = 'loading' | 'success' | 'error';

const QRDownload: React.FC = () => {
  const { pdf_auth_key: pdfAuthKey } = useParams<{ pdf_auth_key: string }>();
  const [status, setStatus] = useState<Status>('loading');
  const [message, setMessage] = useState('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdfAuthKey) {
      setStatus('error');
      setMessage('Invalid download link. Please check your URL.');
      return;
    }

    downloadQRCode(pdfAuthKey);
  }, [pdfAuthKey]);

  const downloadQRCode = async (authKey: string) => {
    setStatus('loading');
    setMessage('');

    try {
      const response = await axiosRubyClient.get('/api/qr/download', {
        params: { pdf_auth_key: authKey },
      });

      if (response.data && response.data.download_url) {
        const url = response.data.download_url;
        setDownloadUrl(url);

        // Automatically trigger download
        window.location.href = url;

        setStatus('success');
        setMessage(
          "Your download should begin shortly. If it doesn't start automatically, click the button below.",
        );
      } else {
        setStatus('error');
        setMessage('Failed to generate download link. Please try again.');
      }
    } catch (err: any) {
      console.error('Download error:', err);
      const errorMessage =
        err.response?.data?.error ||
        'An unexpected error occurred. Please try again.';
      setStatus('error');
      setMessage(errorMessage);
    }
  };

  const handleRetry = () => {
    if (pdfAuthKey) {
      downloadQRCode(pdfAuthKey);
    }
  };

  const handleDownloadAgain = () => {
    if (downloadUrl) {
      window.location.href = downloadUrl;
    }
  };

  return (
    <Container>
      <Content>
        <Title>QR Code Download</Title>

        {status === 'loading' && (
          <LoadingContainer>
            <Spinner />
            <Message>Preparing your download...</Message>
          </LoadingContainer>
        )}

        {status === 'success' && (
          <SuccessContainer>
            <CheckIcon viewBox="0 0 24 24">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
            </CheckIcon>
            <Message>{message}</Message>
            <RetryButton onClick={handleDownloadAgain}>
              Download Again
            </RetryButton>
          </SuccessContainer>
        )}

        {status === 'error' && (
          <ErrorContainer>
            <ErrorIcon viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </ErrorIcon>
            <Message>{message}</Message>
            <SecondaryButton onClick={handleRetry}>Try Again</SecondaryButton>
          </ErrorContainer>
        )}
      </Content>
    </Container>
  );
};

export default QRDownload;
